import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  EmailField,
  DateField,
  Filter,
  TextInput,
  ExportButton,
  ShowButton,
  DeleteButton,
  Show,
  useRedirect,
  TopToolbar,
  ListButton,
} from 'react-admin';
import {
  makeStyles,
  Typography,
  Card,
  CardContent,
  Toolbar,
  Avatar,
  Box,
  Button,
} from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import EmailIcon from '@material-ui/icons/Email';
import PersonIcon from '@material-ui/icons/Person';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const useStyles = makeStyles(theme => ({
  businessCard: {
    boxShadow: theme.shadows[2],
    borderRadius: 8,
    '&:hover': {
      boxShadow: theme.shadows[4],
      transition: 'box-shadow 0.3s ease-in-out',
    },
  },
  list: {
    paddingTop: theme.spacing(2),
  },
  businessHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: '8px 8px 0 0',
    '& svg': {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
      fontSize: '1.5rem',
    },
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5, 1.5),
    fontSize: '0.875rem',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
    },
    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: theme.spacing(0.5),
    },
  },
  contactInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: '#fafafa',
    borderRadius: 6,
    '& svg': {
      marginRight: theme.spacing(1),
      fontSize: '1.2rem',
      color: theme.palette.text.secondary,
    },
  },
  businessDetails: {
    padding: theme.spacing(0, 2, 2, 2),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
    fontSize: '1.1rem',
  },
  filterForm: {
    backgroundColor: '#fafafa',
    marginBottom: theme.spacing(2),
    '& .MuiFormControl-root': {
      backgroundColor: 'white',
    },
  },
  searchInput: {
    marginBottom: 0,
  },
  fieldContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    width: 40,
    height: 40,
    marginRight: theme.spacing(2),
  },
  companyAvatar: {
    backgroundColor: '#eef4ff',
    color: '#4a6dff',
  },
  personAvatar: {
    backgroundColor: '#e4f5e7',
    color: '#34a853',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  primaryText: {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  secondaryText: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  header: {
    '& .MuiTableCell-head': {
      backgroundColor: 'yellow-50',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #e0e0e0',
    }
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
    alignItems: 'flex-start',
  },
  infoLabel: {
    fontWeight: 600,
    minWidth: 140,
    color: theme.palette.text.primary,
    fontSize: '0.9rem',
    marginRight: theme.spacing(1),
  },
  infoValue: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    lineHeight: 1.6,
    flex: 1,
  },
  timestampContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5),
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  requirementBox: {
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(2),
    borderRadius: 6,
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    '& p': {
      margin: 0,
      lineHeight: 1.6,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
  },
}));

const BusinessFormFilter = (props) => {
  const classes = useStyles();
  return (
    <Filter {...props} className={classes.filterForm}>
      <TextInput label="Компанийн нэр" source="orgName" alwaysOn className={classes.searchInput} />
      <TextInput label="Холбогдох хүн" source="firstName" alwaysOn className={classes.searchInput} />
      <TextInput label="И-мэйл" source="orgEmail" alwaysOn className={classes.searchInput} />
      <TextInput label="Ажилтны тоо" source="numberOfEmployees" alwaysOn className={classes.searchInput} />
    </Filter>
  );
};

const BusinessFormActions = props => {
  const {
    filters,
    showFilter,
    resource,
    displayedFilters,
    filterValues,
    currentSort,
    total,
    exporter,
  } = props;

  return (
    <Toolbar>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      <ExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filter={filterValues}
        exporter={exporter}
        maxResults={total}
      />
    </Toolbar>
  );
};

// Company info renderer with icon
const CompanyField = ({ record }) => {
  const classes = useStyles();
  return (
    <Box className={classes.fieldContainer}>
      <Avatar className={`${classes.avatar} ${classes.companyAvatar}`}>
        <BusinessIcon />
      </Avatar>
      <Box className={classes.textContainer}>
        <Typography className={classes.primaryText}>
          {record?.orgName || 'Н/А'}
        </Typography>
        <Typography className={classes.secondaryText}>Компани</Typography>
      </Box>
    </Box>
  );
};

// Contact person field with icon
const ContactPersonField = ({ record }) => {
  const classes = useStyles();
  return (
    <Box className={classes.fieldContainer}>
      <Avatar className={`${classes.avatar} ${classes.personAvatar}`}>
        <PersonIcon />
      </Avatar>
      <Box className={classes.textContainer}>
        <Typography className={classes.primaryText}>
          {record?.firstName || 'Н/А'}
        </Typography>
      </Box>
    </Box>
  );
};

export const BusinessFormList = props => {
  const classes = useStyles();
  return (
    <List 
      {...props} 
      filters={<BusinessFormFilter />} 
      actions={<BusinessFormActions {...props} />}
      title="Бизнес хүсэлтүүд"
      sort={{ field: 'createdAt', order: 'DESC' }}
      className={classes.list}
      perPage={25}
    >
      <Datagrid optimized rowClick="show" classes={{ header: classes.header }}>
        <CompanyField label="Компанийн нэр" source="orgName" />
        <ContactPersonField label="Холбогдох хүн" source="firstName" />
        <EmailField label="И-мэйл" source="orgEmail" />
        <TextField label="Ажилтны тоо" source="numberOfEmployees" />
        <TextField label="Холбогдох мэдээлэл" source="requirement" />
        <DateField label="Огноо" source="createdAt" showTime />
        <ShowButton />
        <DeleteButton />
      </Datagrid>
    </List>
  );
};

const BusinessFormShowActions = ({ basePath, data, resource }) => (
  <TopToolbar>
    <ListButton basePath={basePath} />
    <DeleteButton basePath={basePath} record={data} resource={resource} />
  </TopToolbar>
);

export const BusinessFormShow = props => (
  <Show {...props} actions={<BusinessFormShowActions />} title="Бизнес хүсэлтийн дэлгэрэнгүй">
    <BusinessFormDetails />
  </Show>
);

const BusinessFormDetails = ({ record, basePath }) => {
  const classes = useStyles();
  const redirect = useRedirect();

  // navigate back to the business form list view
  const onBack = () => redirect('list', basePath);

  if (!record) return null;

  return (
    <Card className={classes.businessCard}>
      <div className={classes.businessHeader}>
        <div className={classes.headerTitle}>
          <BusinessIcon />
          <Typography variant="h6">Компанийн мэдээлэл</Typography>
        </div>
        <Button
          startIcon={<ArrowBackIcon />}
          className={classes.backButton}
          onClick={onBack}
        >
          Буцах
        </Button>
      </div>

      <CardContent className={classes.businessDetails}>
        <div className={classes.infoRow}>
          <Typography className={classes.infoLabel}>Компанийн нэр:</Typography>
          <Typography className={classes.infoValue}>{record.orgName}</Typography>
        </div>

        <div className={classes.infoRow}>
          <Typography className={classes.infoLabel}>Ажилтны тоо:</Typography>
          <Typography className={classes.infoValue}>{record.numberOfEmployees}</Typography>
        </div>

        {record.requirement && (
          <div className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Хүсэлтийн дэлгэрэнгүй:</Typography>
            <div className={classes.requirementBox}>
              <Typography className={classes.infoValue}>{record.type}</Typography>
            </div>
          </div>
        )}

        <Typography className={classes.sectionTitle}>
          Холбогдох хүний мэдээлэл
        </Typography>

        <div className={classes.contactInfo}>
          <PersonIcon />
          <Typography>{record.firstName}</Typography>
        </div>

        <div className={classes.contactInfo}>
          <EmailIcon />
          <Typography>{record.orgEmail}</Typography>
        </div>

        <div className={classes.timestampContainer}>
          <Typography className={classes.timestamp}>
            <strong>Илгээсэн:</strong> {new Date(record.createdAt).toLocaleString()}
          </Typography>
          <Typography className={classes.timestamp}>
            <strong>Сүүлд шинэчилсэн:</strong> {new Date(record.updatedAt).toLocaleString()}
          </Typography>
        </div>
      </CardContent>
    </Card>
  );
};